/**
 * useFinancialDataAPI Hook
 * Handles API communication for advanced financial analysis data
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const useFinancialDataAPI = (businessId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);

  /**
   * Get current user's auth token and ID
   */
  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not found');
    }

    return {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      'X-User-Id': user.id,
      'X-Business-Id': businessId.toString()
    };
  };

  /**
   * Load financial data from the backend
   */
  const loadFinancialData = useCallback(async () => {
    if (!businessId) {
      logger.warn('No business ID provided for loading financial data');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${API_URL}/financial-data/${businessId}`,
        { headers }
      );

      if (response.data.exists) {
        logger.info('Financial data loaded from backend', { businessId });
        setLastSaved(new Date(response.data.updated_at));
        return response.data;
      } else {
        logger.info('No financial data found for business', { businessId });
        return null;
      }
    } catch (err) {
      logger.error('Error loading financial data', { error: err, businessId });
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  /**
   * Save financial data to the backend
   */
  const saveFinancialData = useCallback(async (data) => {
    if (!businessId) {
      logger.warn('No business ID provided for saving financial data');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${API_URL}/financial-data/${businessId}`,
        data,
        { headers }
      );

      if (response.data.success) {
        logger.info('Financial data saved successfully', { businessId });
        setLastSaved(new Date());
        return true;
      }

      return false;
    } catch (err) {
      logger.error('Error saving financial data', { error: err, businessId });
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  /**
   * Delete financial data from the backend
   */
  const deleteFinancialData = useCallback(async () => {
    if (!businessId) {
      logger.warn('No business ID provided for deleting financial data');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const headers = await getAuthHeaders();
      const response = await axios.delete(
        `${API_URL}/financial-data/${businessId}`,
        { headers }
      );

      if (response.data.success) {
        logger.info('Financial data deleted successfully', { businessId });
        setLastSaved(null);
        return true;
      }

      return false;
    } catch (err) {
      logger.error('Error deleting financial data', { error: err, businessId });
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  return {
    loadFinancialData,
    saveFinancialData,
    deleteFinancialData,
    loading,
    error,
    lastSaved
  };
};
